import styles from "../SettingsPage.module.css";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";

function buildDisplayLine(displayName?: string, email?: string): string {
  if (displayName) return `Signed in as ${displayName}`;
  if (email) return `Signed in as ${email}`;
  return "Signed in";
}

interface AccountTabProps {
  readonly userEmail: string;
  readonly darkMode: boolean;
  readonly onSignOut: () => void;
  readonly onToggleDarkMode: (next: boolean) => void;
}

export function AccountTab({
  userEmail,
  darkMode,
  onSignOut,
  onToggleDarkMode,
}: Readonly<AccountTabProps>) {
  const appConfig = useOptionalAppConfig();
  const data = appConfig?.data ?? null;
  const studioUser = data?.user;
  const persona = studioUser?.persona ?? null;
  const client = data?.client ?? null;
  const displayLine = buildDisplayLine(studioUser?.displayName, userEmail);
  const subtitleParts: string[] = [];
  if (studioUser?.email) subtitleParts.push(studioUser.email);
  else if (userEmail && !studioUser?.displayName) subtitleParts.push(userEmail);
  if (studioUser?.role) subtitleParts.push(studioUser.role);

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Account</h3>
        <div className={styles.row}>
          <div className={styles.rowInfo}>
            <div className={styles.rowLabel}>{displayLine}</div>
            <div className={styles.rowDesc}>
              {subtitleParts.length > 0
                ? subtitleParts.join(" · ")
                : "Sign out of your AiVA account"}
            </div>
          </div>
          <button type="button" className={styles.dangerPill} onClick={onSignOut}>
            Sign Out
          </button>
        </div>
        {(client || persona) && (
          <div className={styles.row}>
            <div className={styles.rowInfo}>
              <div className={styles.rowLabel}>
                {client?.name ?? "Workspace"}
              </div>
              <div className={styles.rowDesc}>
                {persona ? `Persona: ${persona.name}` : "No persona assigned"}
                {client?.tier ? ` · ${client.tier}` : ""}
              </div>
            </div>
            {persona?.colour && (
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius: 9999,
                  background: persona.colour,
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Appearance</h3>
        <div className={styles.row}>
          <div className={styles.rowInfo}>
            <div className={styles.rowLabel}>Dark Mode</div>
            <div className={styles.rowDesc}>Switch between light and dark theme</div>
          </div>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              role="switch"
              aria-label="Dark mode"
              checked={darkMode}
              onChange={(e) => onToggleDarkMode(e.target.checked)}
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>
      </div>
    </div>
  );
}
